import React, { createContext, useState, useCallback, ReactNode } from 'react';
import { MenuItem } from 'components/dropdowns/RMDropdown';
import useRMLite from 'CustomHooks/useRMLite';

interface RightClickContextValue {
  menuItems: MenuItem[];
  menuPosition: { x: number; y: number } | null;
  addMenuItem: (item: MenuItem) => void;
  removeMenuItem: (item: MenuItem) => void;
  handleRightClick: (event: React.MouseEvent, items?: MenuItem[]) => void;
  closeMenu: () => void;
  replaceMenuItems: (items: MenuItem[]) => void;
}

const RightClickContext = createContext<RightClickContextValue | null>(null);

export const RightClickProvider = ({ children }: { children: ReactNode }) => {
  const [menuItems, setMenuItems] = useState<MenuItem[]>([]);
  const [menuPosition, setMenuPosition] = useState<{ x: number; y: number } | null>(null);
  const isRMLite = useRMLite();

  const addMenuItem = useCallback(
    (item: MenuItem) => {
      if (!isRMLite) {
        setMenuItems((prev) => [...prev, item]);
      }
    },
    [isRMLite],
  );

  const replaceMenuItems = useCallback(
    (items: MenuItem[]) => {
      if (!isRMLite && items != null) {
        setMenuItems(items);
      }
    },
    [isRMLite],
  );

  const removeMenuItem = useCallback((item: MenuItem) => {
    setMenuItems((prev) => prev.filter((i) => i !== item));
  }, []);

  const handleRightClick = useCallback((event: React.MouseEvent, items?: MenuItem[]) => {
    event.preventDefault();
    setMenuPosition({ x: event.clientX, y: event.clientY });
    if (items) {
      setMenuItems(items); // Set custom menu items if provided
    }
  }, []);

  const closeMenu = useCallback(() => {
    setMenuPosition(null);
  }, []);

  return (
    <RightClickContext.Provider
      value={{
        menuItems: isRMLite ? [] : menuItems,
        menuPosition,
        addMenuItem,
        removeMenuItem,
        handleRightClick,
        closeMenu,
        replaceMenuItems,
      }}
    >
      {children}
    </RightClickContext.Provider>
  );
};

/**
 * This Most probably won't directly be used in the components, but it's a helper hook to access the context value
 * @returns {RightClickContextValue} The context value
 */
export const useRightClick = (): RightClickContextValue => {
  const context = React.useContext(RightClickContext);
  if (!context) {
    throw new Error('useRightClick must be used within a RightClickProvider');
  }
  return context;
};
